import React, {
  useState,
  useEffect,
  useLayoutEffect,
  useMemo,
  useCallback,
  useRef,
  type MutableRefObject,
} from 'react';
import {
  dismissQaScrubberItemVisitsVisitIdQaScrubberRuleIdDismissPost,
  getVisitSchemaRogerdocV2VisitsVisitIdSchemaGet,
  listEnabledSectionsBespokeTemplatesEnabledGet,
  restoreQaScrubberItemVisitsVisitIdQaScrubberRuleIdDismissDelete,
  updateVisitTaskDetailAnswersVisitsVisitIdTaskDetailAnswersPut,
  validateOasisVisitsVisitIdValidateOasisPost,
} from '../../services/api/sdk.gen';
import { VisitTypeId } from '../../services/api/models/VisitTypeId';
import {
  OASISSchema,
  OASISFormData,
  Question,
  FormSchemaVersion,
} from './types';
import type {
  ContradictionFieldRef,
  ContradictionFinding,
  EnabledBespokeEntry,
  OasisValidationError,
  RogerDocFormType,
} from '../../services/api/types.gen';
import { OASISQuestionRenderer } from './OASISQuestionRenderer';
import { CmsQuestionErrorsContext, cmsErrorsByQid } from './cmsFieldErrors';
import { ClinicProvider } from './ClinicContext';
import { FormTemplatesProvider } from './FormTemplatesContext';
import { useAgencyFormTemplates } from './useAgencyFormTemplates';
import { OASISNavigation } from './OASISNavigation';
import { VisitActiveDiagnosis } from './VisitActiveDiagnosis';
import { DiagnosisReview } from './DiagnosisReview';
import { CmsIssueCard } from './CmsIssueCard';
import { ContradictionFindingCard } from './contradictions/ContradictionFindingCard';
import {
  findContradictionSourceTargets,
  sectionIdForContradictionSource,
  withModuleCategories,
} from './contradictions/contradictionNavigation';
import {
  buildAdvisoryScrubberItems,
  buildFatalScrubberErrors,
  cmsErrorIsDiagnosisSeverity,
  countActiveScrubberItems,
  countCmsWarningsByCategory,
  countFatalsByCategory,
  countQaFindingsByCategory,
  SCRUBBER_TAB_DEFINITIONS,
  SCRUBBER_TABS,
} from './contradictions/scrubberAdvisory';
import type {
  ScrubberListItem,
  ScrubberTab,
} from './contradictions/scrubberAdvisory';
import {
  escapeCssAttributeValue,
  isVisibleOasisNavigationTarget,
} from './oasisNavigationVisibility';
import { centerInScrollPort } from '../../utils/scrollPort';
import {
  resolveBespokeSection,
  type SchemaQuestionOption,
  type BespokeDbConfig,
} from './bespoke/bespokeRegistry';
import { useVisitExternalAnswers } from './bespoke/layout/useVisitExternalAnswers';
import { pinFromFormSchemaVersion } from '../../pages/formSchema/formSchemaVersionPin';
import { useOASISFormStore } from '../../store/slices/oasisFormSlice';
import { useDebounce } from '../../hooks/useDebounce';
import { useToast } from '../../hooks/useToastContext';
import { useScrollportHeight } from '../../hooks/useScrollportHeight';
import {
  getUserFacingApiMessage,
  isBillingCodesLockedError,
} from '../../utils/apiErrorDetail';
import { getHttpStatus } from '../../utils/httpErrorUtils';
import {
  applyReplacements,
  getOASISFormData,
  getSubitemClearUpdates,
} from './utils';
import {
  BILLING_CODE_QIDS,
  TASK_DETAIL_QIDS,
  withBillingCodeQuestionsLocked,
  withoutTaskDetailAnswers,
} from './taskDetailAnswers';
import { useVisitNoteBillingCodesLocked } from '../../hooks/useVisitNoteBillingCodesLocked';
import { useSyncTaskDetailAnswers } from '../visit/useSyncTaskDetailAnswers';
import {
  resolveQuestionSpan,
  getOasisAutosaveDebounceMs,
  OASIS_INLINE_VALIDATION_DEBOUNCE_MS,
  GridSpan,
  SPAN_CLASSES,
} from './OASISFormUtils';
import { Loader2, AlertTriangle, ChevronDown, ChevronUp } from 'lucide-react';
import { CarryoverSourceMenu } from '../visit/CarryoverSourceMenu';
import { CarryoverStagedBanner } from '../visit/CarryoverStagedBanner';
import { SaveDeclinedError, useSaveStatus } from '../../hooks/useAutoSave';
import type { AutoSaveStatus } from '../../hooks/useAutoSave';
import { useStore } from '../../store';
import { useShallow } from 'zustand/react/shallow';
import { FeatureFlag } from '../../constants/featureFlags';
import { WHOLE_VISIT_SECTION_NAME } from '../../constants/formSections';
import {
  showsEventsSection,
  showsDiagnosisReview,
  showsGoalsSection,
  isSNDiscipline,
  supportsActiveDiagnosisPage,
  supportsWounds,
  getDisciplineForVisitType,
} from '../../constants/visitTypes';
import { visitGoalCapabilities } from '../../utils/goalTaskCapabilities';
import {
  ACTIVE_DIAGNOSIS_CATEGORY,
  DIAGNOSIS_REVIEW_CATEGORY,
  MEDICATIONS_CATEGORY,
  MEDICATIONS_SECTION_ID,
  SKIN_SECTION_ID,
  EVENTS_CATEGORY,
  GOALS_CATEGORY,
  WOUNDS_CATEGORY,
  buildLabelByCategory,
  resolveCategoryLabel,
} from './OASISFormLabels';
import { PatientWounds } from '../wounds';
import { PatientMedications } from '../medications';
import PatientAllergies from '../PatientAllergies';
import { PatientEvents } from '../patient/PatientEvents';
import GoalsTab, { type GoalsTabVisit } from '../goals/GoalsTab';
import { qidsForCmsItems, type ChartFixTarget } from './cmsFixTarget';
import type {
  OrderResponse,
  PatientResponse,
  TaskStatus,
} from '../../services/api/types.gen';

/**
 * Height bounds for the expanded Live Scrubber issue list, in pixels so they
 * stay in the same units as the measured scrollport height. A rem-based cap
 * would disagree with the measured one for anyone who has raised their browser
 * font size, leaving two competing ceilings.
 */
const SCRUBBER_BODY_MAX_HEIGHT_PX = 224;
const SCRUBBER_BODY_MIN_HEIGHT_PX = 96;
/** Share of the visible form area the expanded list may take at most. */
const SCRUBBER_BODY_SCROLLPORT_SHARE = 0.35;

/**
 * Height the pinned section list and Live Scrubber hold back from the
 * scrollport. It has to be at least the bottom padding of the scrolled content
 * that hosts this form, which is `p-5` (20px) on the body of
 * `VisitDetailPanel`; see `useScrollportHeight` for why a smaller value clips
 * the pinned panel's own header at the end of a long form. The remainder is
 * breathing room above the scrollport's bottom edge.
 */
const PINNED_PANEL_GUTTER_PX = 24;

/** Stable no-op so the embedded GoalsTab's `onClose` prop keeps a stable identity. */
const noop = () => {};

/** OASIS M0090, Date Assessment Completed: the coding date. */
const M0090_QID = 'd_completed_9665b2';

/**
 * The M0090 answer as a comparable key. The Active Diagnosis module re-reads
 * its coding date when the *saved* value changes, not the typed one: the
 * server judges codes on what autosave has persisted.
 */
const m0090Key = (data: OASISFormData | undefined): string => {
  const value = data?.[M0090_QID];
  return value == null ? '' : String(value);
};

interface OASISFormProps {
  visitId: string;
  visitType: VisitTypeId;
  /** From API `VisitResponse.is_oasis` / `VISIT_TYPE_INFO.is_oasis` — never infer from `visitType` string shape. */
  isOasis: boolean;
  initialData?: OASISFormData;
  onSave: (data: OASISFormData) => void | Promise<void>;
  hasActiveSignatures?: boolean;
  showInlineValidation?: boolean;
  /**
   * Reports autosave status changes so the parent can render an indicator.
   * changeToken increments on every edit that resets the debounce timer.
   */
  onSaveStatusChange?: (status: AutoSaveStatus, changeToken: number) => void;
  /**
   * Set by the form to a function that sends any unsaved Task Details answers
   * and resolves true once none are left. The panel calls it before Sign.
   */
  taskDetailFlushRef?: MutableRefObject<(() => Promise<boolean>) | null>;
  /**
   * Set by the form to a function that drops its unsaved answers to the given
   * questions. The panel calls it once Task Details saved newer values for them.
   */
  taskDetailDiscardRef?: MutableRefObject<
    ((qids: readonly string[]) => void) | null
  >;
  /**
   * Set by the form to a function the panel calls once it has written the saved
   * Task Details answers into the form.
   */
  taskDetailSyncedRef?: MutableRefObject<(() => void) | null>;
  /** Patient the visit belongs to; used by the embedded wounds and meds modules. */
  patientId: string;
  /**
   * Full patient record; used by the embedded allergies module. Optional so the
   * form still renders while the parent is loading it; the allergies section is
   * withheld until it is available.
   */
  patient?: PatientResponse | null;
  /** Current visit status; gates editing in the embedded modules. */
  visitStatus: TaskStatus;
  /** A CMS error to open the chart at, from a link outside it (OASIS Manager). */
  fixTarget?: ChartFixTarget | null;
  /** Called once the form has acted on `fixTarget`. */
  onFixTargetHandled?: () => void;
  /** True when the visit's period is closed; the embedded modules lock too. */
  episodeClosed?: boolean;
  /** Episode the visit belongs to; scopes the embedded meds module's orders. */
  episodeId?: string;
  /** Episode window; used by the embedded goals section. */
  episodeStartDate?: string | null;
  episodeEndDate?: string | null;
  /** Forwarded to the embedded meds module when a medication order is created. */
  onMedicationOrderCreated?: (order: OrderResponse) => void;
  /**
   * Offer "copy from a previous visit". Only routine visits
   * on a carryover-enabled form, where every source shares this visit's form
   * schema.
   */
  carryoverEnabled?: boolean;
  /**
   * Fires whenever a copy starts or finishes awaiting a decision.
   * While one is pending the copied answers are on screen but deliberately not
   * autosaved, so the surrounding panel must know to keep sign/submit — which
   * act on saved data — from committing the older answers.
   */
  onCarryoverPendingChange?: (pending: boolean) => void;
  /** Clinic the visit belongs to; scopes the attending physician picker. */
  agencyClinicId?: string | null;
  /**
   * Reports the Live Scrubber's fatal count after each validation, so the
   * panel can stop a signature the agency requires a clean chart for.
   */
  onFatalCountChange?: (count: number) => void;
  /**
   * Bump to open the Live Scrubber on the All CMS tab and scroll it into view.
   * 0 (the default) never opens it.
   */
  scrubberFocusRequest?: number;
}

const OASIS_SCRUBBER_HIGHLIGHT_CLASSES = [
  'ring-2',
  'ring-primary',
  'ring-offset-2',
  'rounded-md',
  'transition',
];
const OASIS_SCRUBBER_HIGHLIGHT_MS = 3000;
/** Quiet time before a Task Details answer is sent; it has no dialog to wait on. */
const TASK_DETAIL_ANSWERS_DEBOUNCE_MS = 800;
/** After a dropped connection or a server error, try a Task Details save again. */
const TASK_DETAIL_ANSWERS_RETRY_MS = 5000;
const TASK_DETAIL_ANSWERS_MAX_RETRIES = 3;
const BILLING_CODES_LOCKED_MESSAGE =
  'Location of care and HCPCS can only be changed in Task Details.';

interface ScrubberNavigationTarget {
  qid: string | null;
  element: HTMLElement;
}

interface PendingScrubberNavigation {
  qids: string[];
  attemptedCategories: string[];
}

interface PendingContradictionNavigation {
  location: ContradictionFieldRef;
}

const findVisibleOasisNavigationTarget = (
  qids: string[]
): ScrubberNavigationTarget | null => {
  for (const qid of qids) {
    const classicTarget = document.getElementById(`oasis-q-${qid}`);
    if (
      classicTarget instanceof HTMLElement &&
      isVisibleOasisNavigationTarget(classicTarget)
    ) {
      return { qid, element: classicTarget };
    }

    const safeQid = escapeCssAttributeValue(qid);
    const bespokeTargets = document.querySelectorAll<HTMLElement>(
      `[data-qid="${safeQid}"]`
    );
    for (const bespokeTarget of bespokeTargets) {
      if (isVisibleOasisNavigationTarget(bespokeTarget)) {
        return { qid, element: bespokeTarget };
      }
    }
  }

  return null;
};

const getNextUnattemptedCategory = (
  qids: string[],
  qidToCategoryMap: Record<string, string>,
  attemptedCategories: string[]
) => {
  const attempted = new Set(attemptedCategories);
  for (const qid of qids) {
    const category = qidToCategoryMap[qid];
    if (category && !attempted.has(category)) return category;
  }
  return null;
};

const addOasisNavigationHighlight = (element: HTMLElement) => {
  element.classList.add(...OASIS_SCRUBBER_HIGHLIGHT_CLASSES);
};

const removeOasisNavigationHighlight = (element: HTMLElement) => {
  element.classList.remove(...OASIS_SCRUBBER_HIGHLIGHT_CLASSES);
};

/** A flattened grid item - either a standalone question or context about its parent */
interface GridItem {
  question: Question;
  span: GridSpan;
}

const getDirectSubitemCount = (question: Question): number =>
  Object.values(question.subitems ?? {}).reduce(
    (sum, subitems) => sum + subitems.length,
    0
  );

/**
 * Resolve container span for subgroup parents:
 * - schema width wins
 * - otherwise use a compact default based on how many direct subitems exist
 */
const resolveSubgroupContainerSpan = (question: Question): GridSpan => {
  if (
    question.roger_emr_display_width !== undefined &&
    question.roger_emr_display_width >= 1 &&
    question.roger_emr_display_width <= 12
  ) {
    return question.roger_emr_display_width as GridSpan;
  }

  const directSubitems = getDirectSubitemCount(question);
  if (directSubitems <= 1) return 3;
  if (directSubitems <= 2) return 6;
  if (directSubitems <= 4) return 9;
  return 12;
};

/**
 * Maps each question to a grid item, sizing its span by whether it has subitems.
 */
const flattenQuestions = (questions: Question[]): GridItem[] => {
  return questions.map(question => {
    if (question.subitems && Object.keys(question.subitems).length > 0) {
      return { question, span: resolveSubgroupContainerSpan(question) };
    }
    return { question, span: resolveQuestionSpan(question) };
  });
};

const buildQidToCategoryMap = (schema: OASISSchema | null) => {
  const map: Record<string, string> = {};
  if (!schema) return map;

  const collectQuestion = (question: Question, category: string) => {
    if (question.qid) {
      map[question.qid] = category;
    }
    if (!question.subitems) return;
    Object.values(question.subitems).forEach(subitems => {
      subitems.forEach(subitem => collectQuestion(subitem, category));
    });
  };

  Object.entries(schema).forEach(([category, questions]) => {
    questions.forEach(question => collectQuestion(question, category));
  });

  return map;
};

const buildQidToQuestionText = (schema: OASISSchema | null) => {
  const map: Record<string, string> = {};
  if (!schema) return map;

  const collect = (question: Question) => {
    if (question.qid && question.question) {
      map[question.qid] = question.question;
    }
    if (!question.subitems) return;
    Object.values(question.subitems).forEach(subitems => {
      subitems.forEach(sub => collect(sub));
    });
  };

  Object.values(schema).forEach(questions => {
    questions.forEach(q => collect(q));
  });

  return map;
};

/**
 * Hold the dense 12-column question grid at a usable width without forcing
 * embedded modules (Goals, Meds, Wounds, Diagnoses) onto the same floor.
 * Horizontal overflow stays inside this pocket so right-aligned actions on
 * those modules remain reachable on iPad-width viewports (support 29988).
 * Below `md` fields stack full-width, so no floor.
 *
 * The standard grid's floor is 64rem for the whole form, minus the Sections
 * sidebar (`w-48`) and the content column's `md:pl-5`, which sit outside this
 * pocket. Bespoke layouts reflow their own grids, so they take a lower floor
 * that fits the column at laptop widths. 40rem is set by eye: narrowing a
 * desktop window further squeezed bespoke cards badly, while below `md` they
 * stack and read fine.
 */
function QuestionGridFloor({
  children,
  bespoke = false,
}: {
  children: React.ReactNode;
  bespoke?: boolean;
}) {
  return (
    <div className='overflow-x-auto'>
      <div
        data-question-grid-floor={bespoke ? 'bespoke' : 'standard'}
        className={bespoke ? 'md:min-w-[40rem]' : 'md:min-w-[50.75rem]'}
      >
        {children}
      </div>
    </div>
  );
}

const OASISFormComponent: React.FC<OASISFormProps> = ({
  visitId,
  visitType,
  isOasis,
  initialData,
  onSave,
  hasActiveSignatures = false,
  showInlineValidation = false,
  onSaveStatusChange,
  taskDetailFlushRef,
  taskDetailDiscardRef,
  taskDetailSyncedRef,
  onFatalCountChange,
  scrubberFocusRequest = 0,
  patientId,
  patient,
  visitStatus,
  episodeClosed,
  episodeId,
  episodeStartDate,
  episodeEndDate,
  onMedicationOrderCreated,
  carryoverEnabled = false,
  onCarryoverPendingChange,
  agencyClinicId,
  fixTarget,
  onFixTargetHandled,
}) => {
  const {
    schema,
    formData,
    currentCategory,
    isLoading,
    error,
    schemaVersion,
    setSchema,
    setFormData,
    setCurrentCategory,
    setIsLoading,
    setError,
    setLastSaved,
    setSchemaVersion,
  } = useOASISFormStore();

  const { hasFeatureFlag } = useStore(
    useShallow(state => ({
      hasFeatureFlag: state.hasFeatureFlag,
    }))
  );
  const useVersionedFormSchemas = true;
  // QA findings only exist for flagged agencies, so everyone else would get a
  // permanently empty QA tab.
  const qaTabEnabled = hasFeatureFlag(FeatureFlag.CONTRADICTION_RULES_ENABLED);

  const [categories, setCategories] = useState<string[]>([]);
  // `section_id → display_name` from the active FSV's section_order. Empty
  // map for FSVs that don't ship one yet, in which case `resolveCategoryLabel`
  // falls back to the legacy regex transform.
  const labelByCategory = useMemo(
    () => buildLabelByCategory(schemaVersion),
    [schemaVersion]
  );
  /** Default (null) durations latch outcomes until the next edit; the pill renders them directly. */
  const { saveStatus, changeToken, reportEdit, beginSave, resolveSave } =
    useSaveStatus();
  /** The last M0090 that reached the server; see `m0090Key`. */
  const [savedM0090, setSavedM0090] = useState(() => m0090Key(initialData));
  /**
   * The answers as they stood before a carryover copy was applied,
   * or null when no copy is awaiting a decision.
   *
   * A copy writes straight into `formData` — there is exactly one store of
   * answers, so editing, clearing, and validating a copied answer are ordinary
   * form operations with no second map to reconcile. What makes the copy
   * reversible is this snapshot, not a separate copy of the answers: Discard is
   * "put these back", and it restores the clinician's own pre-copy edits too.
   *
   * Non-null is also the single "a copy is pending" signal: it holds autosave
   * back, keeps the banner up, and blocks sign/submit in the parent.
   */
  const [preCarryoverAnswers, setPreCarryoverAnswers] =
    useState<OASISFormData | null>(null);
  const [carryoverSourceLabel, setCarryoverSourceLabel] = useState<
    string | null
  >(null);
  /** Qids a pending copy wrote, for the "copied" marker on each field. Display only. */
  const [carryoverQids, setCarryoverQids] = useState<ReadonlySet<string>>(
    () => new Set()
  );
  const carryoverPending = preCarryoverAnswers !== null;
  useEffect(() => {
    onCarryoverPendingChange?.(carryoverPending);
  }, [carryoverPending, onCarryoverPendingChange]);
  /** Per-form DB bespoke template map (schema version → section → entry). */
  const externalAnswers = useVisitExternalAnswers(visitId);

  const [bespokeDbConfig, setBespokeDbConfig] = useState<BespokeDbConfig>(
    () => new Map()
  );
  const { error: showError } = useToast();
  const [inlineValidationErrors, setInlineValidationErrors] = useState<
    OasisValidationError[]
  >([]);
  const [inlineContradictions, setInlineContradictions] = useState<
    ContradictionFinding[]
  >([]);
  const [inlineValidationLoading, setInlineValidationLoading] = useState(false);
  const [inlineValidationErrorMessage, setInlineValidationErrorMessage] =
    useState<string | null>(null);
  const [hasValidatedOnce, setHasValidatedOnce] = useState(false);
  const [isScrubberExpanded, setIsScrubberExpanded] = useState(false);
  // Mobile-only collapsible section picker (replaces the desktop side nav)
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);
  // Close the picker whenever the section changes, including navigation
  // driven outside the picker (e.g. Live Scrubber jump-to-question).
  useEffect(() => {
    setIsMobileNavOpen(false);
  }, [currentCategory]);
  // Shared across sections by design — switching section keeps the chosen tab.
  const [scrubberTab, setScrubberTab] = useState<ScrubberTab>('section');
  const [highlightedQid, setHighlightedQid] = useState<string | null>(null);
  const [pendingScrubberNavigation, setPendingScrubberNavigation] =
    useState<PendingScrubberNavigation | null>(null);
  const [pendingContradictionNavigation, setPendingContradictionNavigation] =
    useState<PendingContradictionNavigation | null>(null);
  const hasRunInitialValidation = useRef(false);
  /** Bumped per scrubber request, so a late response from an older one is dropped. */
  const latestValidationRequestRef = useRef(0);

  /** Avoid resetting local edits when parent `initialData` updates after autosave. */
  const hydratedVisitIdRef = useRef<string | null>(null);

  /** The pinned Live Scrubber, which jump targets must land clear of. */
  const scrubberPanelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!showInlineValidation || !isOasis || !hasValidatedOnce) return;
    onFatalCountChange?.(
      inlineValidationErrors.filter(err => err.severity === 'fatal').length
    );
  }, [
    showInlineValidation,
    isOasis,
    hasValidatedOnce,
    inlineValidationErrors,
    onFatalCountChange,
  ]);

  useEffect(() => {
    if (!scrubberFocusRequest) return;
    setScrubberTab('all');
    setIsScrubberExpanded(true);
    scrubberPanelRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  }, [scrubberFocusRequest]);

  const qidToCategoryMap = useMemo(
    () => withModuleCategories(buildQidToCategoryMap(schema), categories),
    [schema, categories]
  );
  const qidToQuestionText = useMemo(
    () => buildQidToQuestionText(schema),
    [schema]
  );
  // Each question shows its own live CMS fatals, worded as the All CMS card
  // and the OASIS Manager's error dialog word them.
  const questionErrors = useMemo(
    () => cmsErrorsByQid(inlineValidationErrors),
    [inlineValidationErrors]
  );
  // Sidebar dots follow the active tab: the CMS tabs and the QA tab are read by
  // different people, so each marks only its own audience's sections.
  const fatalCountByCategory = useMemo(
    () =>
      scrubberTab === 'qa'
        ? {}
        : countFatalsByCategory(inlineValidationErrors, qidToCategoryMap),
    [scrubberTab, inlineValidationErrors, qidToCategoryMap]
  );
  const advisoryCountByCategory = useMemo(
    () =>
      scrubberTab === 'qa'
        ? countQaFindingsByCategory(inlineContradictions, qidToCategoryMap)
        : countCmsWarningsByCategory(inlineValidationErrors, qidToCategoryMap),
    [
      scrubberTab,
      inlineValidationErrors,
      inlineContradictions,
      qidToCategoryMap,
    ]
  );

  /**
   * Deferred work owned by the current highlight. A jump outlives its own call
   * by up to OASIS_SCRUBBER_HIGHLIGHT_MS, so the next jump has to supersede it
   * and an unmount has to cancel it - a timer that fires after the tree is gone
   * sets state on nothing.
   */
  const highlightFrameRef = useRef<number | null>(null);
  const highlightTimeoutsRef = useRef<number[]>([]);
  const highlightedElementsRef = useRef<HTMLElement[]>([]);

  const clearPendingHighlight = useCallback(() => {
    if (highlightFrameRef.current !== null) {
      window.cancelAnimationFrame(highlightFrameRef.current);
      highlightFrameRef.current = null;
    }
    for (const timeoutId of highlightTimeoutsRef.current) {
      window.clearTimeout(timeoutId);
    }
    highlightTimeoutsRef.current = [];
    for (const element of highlightedElementsRef.current) {
      removeOasisNavigationHighlight(element);
    }
    highlightedElementsRef.current = [];
  }, []);

  useEffect(() => clearPendingHighlight, [clearPendingHighlight]);

  /** Highlight every target; scroll to the first. */
  const navigateToResolvedTargets = useCallback(
    (targets: ScrubberNavigationTarget[]) => {
      if (targets.length === 0) return;
      clearPendingHighlight();
      const [scrollTarget] = targets;
      setHighlightedQid(scrollTarget.qid);
      // A cross-section jump resolves its target in the same commit that swaps
      // the section's children, and a smooth scroll requested mid-swap is
      // dropped. A frame puts it after that commit.
      highlightFrameRef.current = window.requestAnimationFrame(() => {
        highlightFrameRef.current = null;
        if (!scrollTarget.element.isConnected) return;
        centerInScrollPort(
          scrollTarget.element,
          scrubberPanelRef.current?.getBoundingClientRect().height ?? 0
        );
      });
      highlightTimeoutsRef.current.push(
        window.setTimeout(() => {
          for (const target of targets) {
            addOasisNavigationHighlight(target.element);
          }
          highlightedElementsRef.current = targets.map(
            target => target.element
          );
        }, 0)
      );
      highlightTimeoutsRef.current.push(
        window.setTimeout(() => {
          clearPendingHighlight();
          setHighlightedQid(null);
        }, OASIS_SCRUBBER_HIGHLIGHT_MS)
      );
    },
    [clearPendingHighlight]
  );

  const navigateToResolvedTarget = useCallback(
    (target: ScrubberNavigationTarget) => navigateToResolvedTargets([target]),
    [navigateToResolvedTargets]
  );

  const navigateToQids = useCallback(
    (qids: string[]) => {
      const uniqueQids = Array.from(new Set(qids.filter(Boolean)));
      if (uniqueQids.length === 0) return false;

      const target = findVisibleOasisNavigationTarget(uniqueQids);
      if (target) {
        setPendingScrubberNavigation(null);
        navigateToResolvedTarget(target);
        return true;
      }

      const attemptedCategories = currentCategory ? [currentCategory] : [];
      const nextCategory = getNextUnattemptedCategory(
        uniqueQids,
        qidToCategoryMap,
        attemptedCategories
      );

      if (!nextCategory) {
        setPendingScrubberNavigation(null);
        return false;
      }

      setPendingScrubberNavigation({
        qids: uniqueQids,
        attemptedCategories: [...attemptedCategories, nextCategory],
      });
      setCurrentCategory(nextCategory);
      return true;
    },
    [
      qidToCategoryMap,
      currentCategory,
      setCurrentCategory,
      navigateToResolvedTarget,
    ]
  );

  const navigateToContradictionLocation = useCallback(
    (location: ContradictionFieldRef) => {
      if (location.qids?.length && navigateToQids(location.qids)) {
        return;
      }

      const sourceTargets = findContradictionSourceTargets(location);
      if (sourceTargets.length > 0) {
        setPendingContradictionNavigation(null);
        navigateToResolvedTargets(
          sourceTargets.map(element => ({ qid: null, element }))
        );
        return;
      }

      // Embedded modules (active dx / wounds / meds) only mount on their
      // section — switch there and let the pending-nav effect wait for the
      // data-contradiction-source anchor.
      // Also pending when we are already on the section: the module may still
      // be fetching the rows this location names.
      const embeddedSection = sectionIdForContradictionSource(
        location.source,
        qidToCategoryMap
      );
      if (embeddedSection) {
        setPendingContradictionNavigation({ location });
        if (currentCategory !== embeddedSection) {
          setCurrentCategory(embeddedSection);
        }
      }
    },
    [
      navigateToQids,
      navigateToResolvedTargets,
      currentCategory,
      setCurrentCategory,
      qidToCategoryMap,
    ]
  );

  const navigateToError = useCallback(
    (err: OasisValidationError) => {
      if (navigateToQids(err.qids || [])) return;
      // Diagnosis severity errors name a diagnosis row, which has no qid to jump
      // to. The Active Diagnosis module is the same embedded target the QA path
      // resolves for `diagnosis_list`, so reuse that navigation -- including its
      // wait for the module to mount and finish fetching.
      if (cmsErrorIsDiagnosisSeverity(err)) {
        navigateToContradictionLocation({
          source: 'diagnosis_list',
          qids: [],
          label: 'Active Diagnosis',
        });
      }
    },
    [navigateToQids, navigateToContradictionLocation]
  );

  // Waits for the schema load to finish (it lists the sections, then opens the
  // first): the jump must come after that. Each target is acted on once, by
  // value, so a re-render that rebuilds an equal target does not jump again.
  const handledFixTarget = useRef<string | null>(null);
  useEffect(() => {
    if (!fixTarget) {
      handledFixTarget.current = null;
      return;
    }
    if (isLoading || !schema || categories.length === 0) return;
    const key = JSON.stringify(fixTarget);
    if (handledFixTarget.current === key) return;
    handledFixTarget.current = key;
    const qids = [
      ...fixTarget.qids,
      ...qidsForCmsItems(schema, fixTarget.items),
    ];
    if (!navigateToQids(qids) && fixTarget.diagnoses) {
      navigateToContradictionLocation({
        source: 'diagnosis_list',
        qids: [],
        label: 'Active Diagnosis',
      });
    }
    onFixTargetHandled?.();
  }, [
    fixTarget,
    isLoading,
    schema,
    categories,
    navigateToQids,
    navigateToContradictionLocation,
    onFixTargetHandled,
  ]);

  useLayoutEffect(() => {
    if (!pendingScrubberNavigation) return;

    const target = findVisibleOasisNavigationTarget(
      pendingScrubberNavigation.qids
    );
    if (target) {
      navigateToResolvedTarget(target);
      setPendingScrubberNavigation(null);
      return;
    }

    const nextCategory = getNextUnattemptedCategory(
      pendingScrubberNavigation.qids,
      qidToCategoryMap,
      pendingScrubberNavigation.attemptedCategories
    );
    if (!nextCategory) {
      setPendingScrubberNavigation(null);
      return;
    }

    setPendingScrubberNavigation({
      qids: pendingScrubberNavigation.qids,
      attemptedCategories: [
        ...pendingScrubberNavigation.attemptedCategories,
        nextCategory,
      ],
    });
    setCurrentCategory(nextCategory);
  }, [
    pendingScrubberNavigation,
    qidToCategoryMap,
    setCurrentCategory,
    navigateToResolvedTarget,
  ]);

  useEffect(() => {
    if (!pendingContradictionNavigation) return;
    const { location } = pendingContradictionNavigation;

    // Embedded module anchors mount after the section switch (and Active
    // Diagnosis may still be spinning). Retry until the
    // data-contradiction-source node appears or we time out.
    const tryResolve = () => {
      const sourceTargets = findContradictionSourceTargets(location);
      if (sourceTargets.length === 0) return false;
      navigateToResolvedTargets(
        sourceTargets.map(element => ({ qid: null, element }))
      );
      setPendingContradictionNavigation(null);
      return true;
    };

    if (tryResolve()) return;

    const intervalId = window.setInterval(() => {
      if (tryResolve()) {
        window.clearInterval(intervalId);
        window.clearTimeout(timeoutId);
      }
    }, 50);
    const timeoutId = window.setTimeout(() => {
      window.clearInterval(intervalId);
      setPendingContradictionNavigation(null);
    }, 3000);

    return () => {
      window.clearInterval(intervalId);
      window.clearTimeout(timeoutId);
    };
  }, [
    pendingContradictionNavigation,
    currentCategory,
    navigateToResolvedTargets,
  ]);

  /**
   * Persist `data`, reporting whether it actually reached the server.
   *
   * Autosave callers ignore the result — a failed autosave retries on the next
   * edit. Carryover's Keep does not: it has to know, because until the save
   * lands the copied answers exist only in this component.
   */
  const saveFormData = async (
    data: OASISFormData
  ): Promise<'saved' | 'declined' | 'error'> => {
    const saveToken = beginSave();
    try {
      // Task Details answers are saved by their own route, never as chart work.
      const dataToSave = withoutTaskDetailAnswers(
        schema ? getOASISFormData(data, schema) : data
      );
      await onSave(dataToSave);
      setLastSaved(new Date());
      setSavedM0090(m0090Key(data));
      resolveSave(saveToken, 'saved');
      // The connection is back: send Task Details answers a dropped one
      // stranded, and re-read a refused code that could not be re-read.
      if (
        billingCodesStaleRef.current ||
        (taskDetailRetryRef.current.retryable &&
          Object.keys(pendingTaskDetailAnswersRef.current).length > 0)
      ) {
        void saveTaskDetailAnswers();
      }
      return 'saved';
    } catch (error) {
      if (error instanceof SaveDeclinedError) {
        // The user declined the signature-invalidation dialog. The edits
        // are still unsaved (the parent discards them by remounting this
        // form), but nothing failed, so don't report an error.
        resolveSave(saveToken, 'declined');
        return 'declined';
      }
      console.error('Auto-save failed:', error);
      resolveSave(saveToken, 'error');
      return 'error';
    }
  };

  // Debounced save function (save after 2 seconds of inactivity, or immediately if there are signatures)
  const debouncedSave = useDebounce((data: OASISFormData) => {
    void saveFormData(data);
  }, getOasisAutosaveDebounceMs(hasActiveSignatures));

  // Task Details answers the user has changed and that have not reached the
  // server. Only these are sent, so a value someone else changed in Task
  // Details is never overwritten by this form's older copy of it.
  const pendingTaskDetailAnswersRef = useRef<Record<string, unknown>>({});
  // The request in flight, if any. Requests run one at a time, so an older one
  // can never fail after a newer one landed and put its stale value back.
  const taskDetailSaveRef = useRef<Promise<boolean> | null>(null);
  // Questions Task Details saved while the request in flight was out. If it
  // fails, its answers to these are older than what is saved and are dropped.
  const supersededTaskDetailQidsRef = useRef<Set<string>>(new Set());
  const taskDetailRetryRef = useRef<{
    timer: ReturnType<typeof setTimeout> | null;
    attempts: number;
    retryable: boolean;
  }>({ timer: null, attempts: 0, retryable: false });
  // A chart save reporting "Saved" must not hide that these did not save.
  const [taskDetailSaveFailed, setTaskDetailSaveFailed] = useState(false);
  // The server refused a location of care or HCPCS change. The ref is read
  // synchronously so an edit in the same tick cannot queue a code; the state
  // re-renders the fields read-only. Neither is reset while the form is open.
  const codesLockedRef = useRef(false);
  const [codesLockedByServer, setCodesLockedByServer] = useState(false);
  const codesLockedByAgency = useVisitNoteBillingCodesLocked(isOasis);
  const codesLocked = codesLockedByAgency || codesLockedByServer;
  const syncBillingCodeAnswers = useSyncTaskDetailAnswers(
    visitId,
    BILLING_CODE_QIDS
  );
  // The form still shows a refused code because re-reading the saved one
  // failed. Every flush retries the read and reports failure until it lands,
  // but still sends the other answers.
  const billingCodesStaleRef = useRef(false);

  const sendPendingTaskDetailAnswers = async (
    rereadCodes = true
  ): Promise<boolean> => {
    // Only the Task Details failure state changes here: no chart save ran.
    if (
      rereadCodes &&
      billingCodesStaleRef.current &&
      (await syncBillingCodeAnswers())
    ) {
      billingCodesStaleRef.current = false;
      if (Object.keys(pendingTaskDetailAnswersRef.current).length === 0) {
        setTaskDetailSaveFailed(false);
      }
    }
    const answers = pendingTaskDetailAnswersRef.current;
    if (Object.keys(answers).length === 0) return !billingCodesStaleRef.current;
    pendingTaskDetailAnswersRef.current = {};
    supersededTaskDetailQidsRef.current = new Set();
    const retry = taskDetailRetryRef.current;
    if (retry.timer) clearTimeout(retry.timer);
    retry.timer = null;
    const saveToken = beginSave();
    try {
      await updateVisitTaskDetailAnswersVisitsVisitIdTaskDetailAnswersPut({
        path: { visit_id: visitId },
        body: { answers },
        throwOnError: true,
      });
      retry.attempts = 0;
      retry.retryable = false;
      const stale = billingCodesStaleRef.current;
      setTaskDetailSaveFailed(stale);
      setLastSaved(new Date());
      resolveSave(saveToken, 'saved');
      return !stale;
    } catch (error) {
      console.error('Saving task detail answers failed:', error);
      const superseded = supersededTaskDetailQidsRef.current;
      const unsaved = Object.fromEntries(
        Object.entries(answers).filter(([qid]) => !superseded.has(qid))
      );
      if (
        isBillingCodesLockedError(error) &&
        Object.keys(answers).some(qid => BILLING_CODE_QIDS.has(qid))
      ) {
        codesLockedRef.current = true;
        setCodesLockedByServer(true);
        const remaining = {
          ...unsaved,
          ...pendingTaskDetailAnswersRef.current,
        };
        BILLING_CODE_QIDS.forEach(qid => delete remaining[qid]);
        pendingTaskDetailAnswersRef.current = remaining;
        showError(BILLING_CODES_LOCKED_MESSAGE);
        // Before returning, so Sign never opens over the refused value.
        if (!(await syncBillingCodeAnswers())) {
          billingCodesStaleRef.current = true;
          retry.attempts = 0;
          retry.retryable = false;
          setTaskDetailSaveFailed(true);
          resolveSave(saveToken, 'saved');
          if (Object.keys(remaining).length > 0) {
            await sendPendingTaskDetailAnswers(false);
          }
          return false;
        }
        resolveSave(saveToken, 'saved');
        if (Object.keys(remaining).length === 0) {
          setTaskDetailSaveFailed(false);
          retry.retryable = false;
          return true;
        }
        // Called directly: saveTaskDetailAnswers is awaiting this request, so chaining
        // through it would wait on itself. The remainder holds no code, so this runs once.
        return await sendPendingTaskDetailAnswers();
      }
      // Task Details saved newer values for all of them, so nothing is lost.
      if (Object.keys(unsaved).length === 0) {
        resolveSave(saveToken, 'saved');
        return !billingCodesStaleRef.current;
      }
      // Still unsaved. An answer changed since this request went out is newer.
      pendingTaskDetailAnswersRef.current = {
        ...unsaved,
        ...pendingTaskDetailAnswersRef.current,
      };
      setTaskDetailSaveFailed(true);
      resolveSave(saveToken, 'error');
      // A refusal (a value the server cannot use, or a chart that is now
      // read-only) fails the same way again; a dropped connection may not.
      const status = getHttpStatus(error);
      retry.retryable = status === undefined || status >= 500;
      if (retry.retryable && retry.attempts < TASK_DETAIL_ANSWERS_MAX_RETRIES) {
        retry.attempts += 1;
        retry.timer = setTimeout(
          () => void saveTaskDetailAnswersRef.current(),
          TASK_DETAIL_ANSWERS_RETRY_MS
        );
      }
      return false;
    }
  };
  /** Send any pending answers once the request in flight settles; true when none are left. */
  const saveTaskDetailAnswers = (): Promise<boolean> => {
    const run = (taskDetailSaveRef.current ?? Promise.resolve(true)).then(() =>
      sendPendingTaskDetailAnswers()
    );
    taskDetailSaveRef.current = run;
    void run.finally(() => {
      if (taskDetailSaveRef.current === run) taskDetailSaveRef.current = null;
    });
    return run;
  };
  // Timers and the unmount flush call through this, so they use the latest closure.
  const saveTaskDetailAnswersRef = useRef(saveTaskDetailAnswers);
  saveTaskDetailAnswersRef.current = saveTaskDetailAnswers;
  const debouncedSaveTaskDetailAnswers = useDebounce(() => {
    void saveTaskDetailAnswers();
  }, TASK_DETAIL_ANSWERS_DEBOUNCE_MS);
  // Leaving the form inside the debounce window must not lose the answer. A
  // retry it schedules still runs after unmount, on purpose: it is what saves
  // an answer a dropped connection stranded as the user left the visit.
  useEffect(() => () => void saveTaskDetailAnswersRef.current(), []);
  // Task Details saved newer values for these questions, so this form's unsaved
  // answers to them are older and must not be sent over the top.
  const discardTaskDetailAnswers = (qids: readonly string[]) => {
    const pending = { ...pendingTaskDetailAnswersRef.current };
    qids.forEach(qid => {
      delete pending[qid];
      supersededTaskDetailQidsRef.current.add(qid);
    });
    pendingTaskDetailAnswersRef.current = pending;
    if (Object.keys(pending).length > 0) return;
    const retry = taskDetailRetryRef.current;
    if (retry.timer) clearTimeout(retry.timer);
    retry.timer = null;
    retry.attempts = 0;
    retry.retryable = false;
    // The form may still show a refused code.
    setTaskDetailSaveFailed(billingCodesStaleRef.current);
  };
  const discardTaskDetailAnswersRef = useRef(discardTaskDetailAnswers);
  discardTaskDetailAnswersRef.current = discardTaskDetailAnswers;
  useEffect(() => {
    if (!taskDetailDiscardRef) return;
    taskDetailDiscardRef.current = qids =>
      discardTaskDetailAnswersRef.current(qids);
    return () => {
      taskDetailDiscardRef.current = null;
    };
  }, [taskDetailDiscardRef]);
  // The panel wrote the saved answers into the form, so a refused code it
  // could not re-read is gone from the screen.
  const taskDetailAnswersSynced = () => {
    if (!billingCodesStaleRef.current) return;
    billingCodesStaleRef.current = false;
    if (Object.keys(pendingTaskDetailAnswersRef.current).length === 0) {
      setTaskDetailSaveFailed(false);
    }
  };
  const taskDetailAnswersSyncedRef = useRef(taskDetailAnswersSynced);
  taskDetailAnswersSyncedRef.current = taskDetailAnswersSynced;
  useEffect(() => {
    if (!taskDetailSyncedRef) return;
    taskDetailSyncedRef.current = () => taskDetailAnswersSyncedRef.current();
    return () => {
      taskDetailSyncedRef.current = null;
    };
  }, [taskDetailSyncedRef]);
  // Lets the panel flush these before Sign: a signed chart refuses them.
  useEffect(() => {
    if (!taskDetailFlushRef) return;
    taskDetailFlushRef.current = () => saveTaskDetailAnswersRef.current();
    return () => {
      taskDetailFlushRef.current = null;
    };
  }, [taskDetailFlushRef]);

  useEffect(() => {
    // A pending copy holds autosave back, so its edits read as "Not saved"
    // rather than a "Saving..." countdown that never runs.
    const status =
      taskDetailSaveFailed && (saveStatus === 'saved' || saveStatus === 'idle')
        ? 'error'
        : carryoverPending && saveStatus === 'unsaved'
          ? 'blocked'
          : saveStatus;
    onSaveStatusChange?.(status, changeToken);
  }, [
    saveStatus,
    taskDetailSaveFailed,
    carryoverPending,
    changeToken,
    onSaveStatusChange,
  ]);

  /**
   * Per-rule dismissal state this client knows better than a given validation
   * response. Two cases, and only these two:
   *   - `pending`: a dismiss/restore is in flight, so the user's intent is not
   *     on the server yet. Its desired value wins over any response.
   *   - `settled`: a mutation has landed, tagged with the version it landed at.
   *     A validation request that started *before* that version was answered
   *     from pre-mutation state, so its answer for that rule is stale.
   * A validation response that started after a rule's last mutation is
   * authoritative for it — that is what lets another tab's change through
   * instead of pinning the old value until reload.
   */
  const pendingDismissalsRef = useRef(new Map<string, boolean>());
  const settledDismissalsRef = useRef(
    new Map<string, { dismissed: boolean; version: number }>()
  );
  const dismissalVersionRef = useRef(0);

  const applyKnownDismissals = useCallback(
    (findings: ContradictionFinding[]): ContradictionFinding[] =>
      findings.map(finding => {
        const pending = pendingDismissalsRef.current.get(finding.rule_id);
        const settled = settledDismissalsRef.current.get(finding.rule_id);
        const known = pending ?? settled?.dismissed;
        if (known === undefined || finding.dismissed === known) return finding;
        return { ...finding, dismissed: known };
      }),
    []
  );

  /**
   * Forget every settled mutation a validation request already saw. Its answer
   * for those rules is at least as new as ours, so keeping them would let an
   * old click override the server again the next time anything reconciles —
   * which is how dismissing one finding could re-hide an unrelated one that had
   * since been restored elsewhere.
   */
  const forgetDismissalsSeenBy = useCallback((requestedAtVersion: number) => {
    for (const [ruleId, entry] of settledDismissalsRef.current) {
      if (entry.version <= requestedAtVersion) {
        settledDismissalsRef.current.delete(ruleId);
      }
    }
  }, []);

  const runInlineValidation = useCallback(
    async (data: OASISFormData) => {
      // `showInlineValidation` says this surface wants the scrubber; `isOasis`
      // says the visit qualifies for one. Without both, a non-OASIS visit POSTs
      // /validateOasis and gets a 400.
      if (!showInlineValidation || !isOasis || !visitId || !schema) return;

      setInlineValidationLoading(true);
      setInlineValidationErrorMessage(null);
      const requestedAtVersion = dismissalVersionRef.current;
      const requestId = ++latestValidationRequestRef.current;
      const isStale = () => requestId !== latestValidationRequestRef.current;

      try {
        const response = await validateOasisVisitsVisitIdValidateOasisPost({
          path: { visit_id: visitId },
          body: {
            answers: getOASISFormData(data, schema) as Record<string, unknown>,
          },
        });
        if (isStale()) return;

        if (response.error) {
          setInlineValidationErrorMessage(
            getUserFacingApiMessage(
              response,
              'Validation failed. Please try again.'
            )
          );
          setInlineValidationErrors([]);
          setInlineContradictions([]);
        } else {
          setInlineValidationErrors(response.data?.errors || []);
          forgetDismissalsSeenBy(requestedAtVersion);
          setInlineContradictions(
            applyKnownDismissals(response.data?.contradictions || [])
          );
        }
      } catch (err) {
        console.error('Inline validation failed:', err);
        if (isStale()) return;
        setInlineValidationErrorMessage('Validation failed. Please try again.');
        setInlineValidationErrors([]);
        setInlineContradictions([]);
      } finally {
        if (!isStale()) {
          setInlineValidationLoading(false);
          setHasValidatedOnce(true);
        }
      }
    },
    [
      applyKnownDismissals,
      forgetDismissalsSeenBy,
      isOasis,
      schema,
      showInlineValidation,
      visitId,
    ]
  );

  /**
   * Dismiss or restore one QA finding. Flips the local flag first so
   * the list and counts move immediately, and puts it back if the call fails —
   * a re-validation would otherwise be needed to see the result of a click.
   */
  const setContradictionDismissed = useCallback(
    async (ruleId: string, dismissed: boolean) => {
      if (!visitId) return;

      const applyDismissed = (value: boolean) =>
        setInlineContradictions(current =>
          current.map(finding =>
            finding.rule_id === ruleId
              ? { ...finding, dismissed: value }
              : finding
          )
        );

      pendingDismissalsRef.current.set(ruleId, dismissed);
      applyDismissed(dismissed);
      try {
        const call = dismissed
          ? dismissQaScrubberItemVisitsVisitIdQaScrubberRuleIdDismissPost
          : restoreQaScrubberItemVisitsVisitIdQaScrubberRuleIdDismissDelete;
        const response = await call({
          path: { visit_id: visitId, rule_id: ruleId },
        });
        if (response.error) throw response.error;
        // Reconcile against the visit's full dismissed list rather than trusting
        // the optimistic flip: this is why the endpoints return the whole set.
        // Recorded per rule, not as a whole-set replacement — several findings
        // can be dismissed at once, and a slower response must not stamp its
        // own snapshot over a rule another call already settled.
        const serverDismissed = new Set(
          response.data?.dismissed_rule_ids || []
        );
        const version = ++dismissalVersionRef.current;
        pendingDismissalsRef.current.delete(ruleId);
        settledDismissalsRef.current.set(ruleId, {
          dismissed: serverDismissed.has(ruleId),
          version,
        });
        setInlineContradictions(current => applyKnownDismissals(current));
      } catch (err) {
        console.error('QA scrubber dismissal failed:', err);
        pendingDismissalsRef.current.delete(ruleId);
        applyDismissed(!dismissed);
        showError(
          dismissed ? 'Failed to dismiss item' : 'Failed to restore item'
        );
      }
    },
    [applyKnownDismissals, showError, visitId]
  );

  const debouncedInlineValidation = useDebounce((data: OASISFormData) => {
    void runInlineValidation(data);
  }, OASIS_INLINE_VALIDATION_DEBOUNCE_MS);

  // Contradiction findings are built from a server-side snapshot that reads
  // diagnoses, wounds, and medications straight from the database
  // (`build_contradiction_snapshot`), so edits made in those embedded modules
  // change scrubber output without touching a single OASIS answer. Re-validate
  // on each of them or the Live Scrubber keeps showing the stale snapshot until
  // the next OASIS edit or reload.
  const handleScrubberInputChanged = useCallback(() => {
    if (!showInlineValidation) return;
    debouncedInlineValidation(useOASISFormStore.getState().formData);
  }, [debouncedInlineValidation, showInlineValidation]);

  const scrubberExpandedBodyRef = useRef<HTMLDivElement>(null);
  const scrubberStableBodyHeightRef = useRef(0);

  useLayoutEffect(() => {
    if (!showInlineValidation || !isOasis) return;
    if (!isScrubberExpanded) {
      scrubberStableBodyHeightRef.current = 0;
      return;
    }
    if (inlineValidationLoading) return;
    const el = scrubberExpandedBodyRef.current;
    if (!el) return;
    scrubberStableBodyHeightRef.current = el.offsetHeight;
  }, [
    showInlineValidation,
    isOasis,
    isScrubberExpanded,
    inlineValidationLoading,
    inlineValidationErrors,
    inlineValidationErrorMessage,
    hasValidatedOnce,
    scrubberTab,
    currentCategory,
  ]);

  useEffect(() => {
    setCurrentCategory(null);
  }, [visitType, setCurrentCategory]);

  useEffect(() => {
    // A load whose effect was torn down must not land: it would reopen the
    // first section after a fix target already jumped elsewhere.
    let cancelled = false;
    const fetchSchema = async () => {
      setIsLoading(true);
      setError(null);

      try {
        // Always assigned: `useVersionedFormSchemas` is constant `true`, so the
        // versioned branch below always runs (definite-assignment assertion).
        let schemaData!: OASISSchema;
        // Captured from the versioned response so `categoryList` below can
        // honor `section_order` without round-tripping through Zustand
        // (state setters are async, the local `schemaVersion` closed over
        // at render time is stale here).
        let fetchedVersion: FormSchemaVersion | null = null;

        if (useVersionedFormSchemas) {
          const response = await getVisitSchemaRogerdocV2VisitsVisitIdSchemaGet(
            {
              path: { visit_id: visitId },
            }
          );
          if (cancelled) return;

          if (!response?.data) {
            throw new Error(
              'Could not load the required form schema. Please contact support.'
            );
          }

          const { version, schema_data } = response.data as {
            version: FormSchemaVersion;
            schema_data: OASISSchema;
          };

          schemaData = schema_data;
          fetchedVersion = version;
          setSchemaVersion(version);

          const bespokePin = pinFromFormSchemaVersion(
            version.form_type as RogerDocFormType,
            version
          );
          if (!bespokePin) {
            throw new Error(
              'The visit schema is missing form type or version metadata required to load customized section layouts. Please contact support.'
            );
          }

          const bespokeRes =
            await listEnabledSectionsBespokeTemplatesEnabledGet({
              query: {
                form_type: bespokePin.form_type,
                schema_version_name: bespokePin.schema_version_name,
                form_schema_version: bespokePin.form_schema_version,
                // A completed visit renders the layouts it was signed with.
                visit_id: visitId,
              },
            });
          if (cancelled) return;
          const entries =
            (bespokeRes?.data as EnabledBespokeEntry[] | undefined) ?? [];
          const sectionMap = new Map(
            entries.map(e => [
              e.section_name,
              {
                section_name: e.section_name,
                layout_json:
                  (e.layout_json as
                    import('./bespoke/layout/types').LayoutNode[] | null) ??
                  null,
              },
            ])
          );
          setBespokeDbConfig(() => new Map([[version.name, sectionMap]]));
        }

        setSchema(schemaData);

        // Prefer the FormSchemaVersion's `section_order` for both the section list
        // and its order.
        //
        //   1. Filter `section_order` to keys that actually exist in
        //      `schemaData` — an FSV that lists a section the build response
        //      doesn't populate must not produce an empty tab.
        //   2. Append any `schemaData` keys the FormSchemaVersion's `section_order`
        //      omits — in case we have typos or missed a section in section_order.
        const categoryList = (fetchedVersion?.section_order ?? [])
          .map(item => item.section_id)
          .filter(sectionId => sectionId in schemaData);
        for (const key of Object.keys(schemaData)) {
          if (!categoryList.includes(key)) categoryList.push(key);
        }
        // A one-section form renders as a single page with its clinical modules
        // at the bottom. A paginated form has no bottom of the form, so each
        // module goes under the section that hosts it or gets its own page.
        const isPaginated = categoryList.length > 1;
        if (supportsActiveDiagnosisPage(visitType)) {
          categoryList.push(ACTIVE_DIAGNOSIS_CATEGORY);
        }
        if (isPaginated) {
          if (
            isSNDiscipline(visitType) &&
            !categoryList.includes(MEDICATIONS_SECTION_ID)
          ) {
            categoryList.push(MEDICATIONS_CATEGORY);
          }
          if (
            supportsWounds(visitType) &&
            !categoryList.includes(SKIN_SECTION_ID)
          ) {
            categoryList.push(WOUNDS_CATEGORY);
          }
          if (showsDiagnosisReview(visitType)) {
            categoryList.push(DIAGNOSIS_REVIEW_CATEGORY);
          }
          // Transfer and death notes can neither create goals nor record
          // progress against them, so they get no Goals page.
          if (showsGoalsSection(visitType)) {
            categoryList.push(GOALS_CATEGORY);
          }
          if (showsEventsSection(visitType)) {
            categoryList.push(EVENTS_CATEGORY);
          }
        }
        setCategories(categoryList);
        if (categoryList.length > 0) {
          setCurrentCategory(categoryList[0]);
        }
      } catch (err) {
        if (cancelled) return;
        console.error('Failed to fetch question schema:', err);
        setError('Failed to load form schema. Please try again.');
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    fetchSchema();
    return () => {
      cancelled = true;
    };
  }, [
    visitId,
    visitType,
    useVersionedFormSchemas,
    setSchema,
    setIsLoading,
    setError,
    setCurrentCategory,
    setSchemaVersion,
    setCategories,
    setBespokeDbConfig,
  ]);

  // A pending copy is per-visit; dropping it on visit navigation keeps it from
  // bleeding through the (un-keyed) global store.
  useEffect(() => {
    setPreCarryoverAnswers(null);
    setCarryoverSourceLabel(null);
    setCarryoverQids(new Set());
  }, [visitId]);

  // Reload/close warning. In-app route changes are NOT blocked: react-router
  // `useBlocker` needs a data router and this app does not use one.
  useEffect(() => {
    if (!carryoverPending) return;
    const handler = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [carryoverPending]);

  useEffect(() => {
    hydratedVisitIdRef.current = null;
    hasRunInitialValidation.current = false;
    // Seed answers immediately on visit navigation so the UI is not blank while
    // schema/bespoke layouts load. `initialData` is intentionally omitted from
    // deps — this runs once per visitId (OASISForm remounts via key= anyway).
    setFormData(initialData ?? {});
  }, [visitId, setFormData]);

  useEffect(() => {
    if (!schema || isLoading) return;

    // Versioned visits load bespoke layouts in a separate React state update
    // from schema (Zustand). Wait until fetchSchema has populated bespokeDbConfig.
    if (
      useVersionedFormSchemas &&
      schemaVersion?.name &&
      bespokeDbConfig.size === 0
    ) {
      return;
    }

    if (hydratedVisitIdRef.current !== visitId) {
      hydratedVisitIdRef.current = visitId;

      // A visit answered before a question was replaced loads answered.
      const hydrated = applyReplacements(initialData || {}, schema);
      setFormData(hydrated);

      if (showInlineValidation) {
        hasRunInitialValidation.current = true;
        void runInlineValidation(hydrated);
      }
      return;
    }

    if (showInlineValidation && !hasRunInitialValidation.current) {
      hasRunInitialValidation.current = true;
      void runInlineValidation(useOASISFormStore.getState().formData);
    }
    /* `initialData` is omitted on purpose: autosave updates `visit.visit_data` while the user may have newer edits; re-hydrating would flash stale values. */
  }, [
    visitId,
    schema,
    isLoading,
    bespokeDbConfig,
    useVersionedFormSchemas,
    schemaVersion?.name,
    setFormData,
    showInlineValidation,
    runInlineValidation,
  ]);

  /**
   * Room available to the pinned section list and Live Scrubber. Both pin to the
   * top of whichever ancestor scrolls, and anything taller than that ancestor
   * can never be scrolled into view, so both cap themselves against this and
   * scroll internally instead.
   */
  const {
    ref: scrollportRef,
    height: scrollportHeight,
    scrollToTop,
  } = useScrollportHeight(PINNED_PANEL_GUTTER_PX);

  const handleCategoryChange = (category: string) => {
    setCurrentCategory(category);
    // Now that the section list stays reachable from anywhere in the form, a
    // switch can be made from deep in the previous section, which would
    // otherwise open the next one already scrolled past its heading. Scrubber
    // jumps set the category directly instead, so they keep their own target.
    scrollToTop();
  };

  /**
   * Cap for the expanded issue list. Expanding is deliberate, but a pinned panel
   * must still leave the form the larger share of the screen, so the standing
   * ceiling becomes an upper bound rather than the actual height. Before the
   * first measurement lands the ceiling applies on its own.
   */
  const scrubberBodyMaxHeight = scrollportHeight
    ? Math.max(
        SCRUBBER_BODY_MIN_HEIGHT_PX,
        Math.min(
          SCRUBBER_BODY_MAX_HEIGHT_PX,
          Math.round(scrollportHeight * SCRUBBER_BODY_SCROLLPORT_SHARE)
        )
      )
    : SCRUBBER_BODY_MAX_HEIGHT_PX;

  const handleFormDataChange = (incoming: Partial<OASISFormData>) => {
    const updates =
      codesLockedByAgency || codesLockedRef.current
        ? Object.fromEntries(
            Object.entries(incoming).filter(
              ([qid]) => !BILLING_CODE_QIDS.has(qid)
            )
          )
        : incoming;
    // Every answer in the batch was a locked code: nothing changed.
    if (updates !== incoming && Object.keys(updates).length === 0) return;
    const newFormData: OASISFormData = { ...formData };
    Object.entries(updates).forEach(([key, value]) => {
      if (value === undefined) {
        delete newFormData[key];
      } else {
        newFormData[key] = value;
      }
    });

    // An answer kept in Task Details saves through its own route. It is not
    // chart work, so by itself it starts no chart save and breaks no signature.
    const changedQids = Object.keys(updates);
    const taskDetailQids = changedQids.filter(qid => TASK_DETAIL_QIDS.has(qid));
    if (taskDetailQids.length > 0) {
      taskDetailQids.forEach(qid => {
        pendingTaskDetailAnswersRef.current[qid] = updates[qid] ?? '';
      });
      debouncedSaveTaskDetailAnswers();
    }
    const chartChanged = taskDetailQids.length < changedQids.length;

    if (changedQids.length > 0) {
      setFormData(newFormData);
      reportEdit();
      if (!chartChanged) {
        // Nothing for the chart save to send.
      } else if (carryoverPending) {
        // While a copy awaits Keep or Discard, nothing on this form autosaves.
        // The copied answers are in `formData` now, so an autosave would commit
        // them without the clinician deciding — and Discard could no longer
        // take them back. Keep and Discard are the only writes while the banner
        // is up, and both save explicitly.
        debouncedSave.cancel();
      } else {
        debouncedSave(newFormData);
      }
    }

    if (showInlineValidation) {
      debouncedInlineValidation(newFormData);
    }
  };

  /**
   * Apply a copy to the answers on screen. Does not save.
   *
   * The source wins outright across everything it governs: `copyableQids` is
   * cleared first, then the source's answers written over the top. A question
   * the source left blank therefore ends up blank here rather than keeping this
   * visit's older answer under a copied one. Questions outside that list —
   * vitals, narratives, time in/out — are not touched.
   */
  const handleCarryoverLoaded = useCallback(
    (
      answers: Record<string, unknown>,
      copyableQids: string[],
      label: string
    ) => {
      // Anything queued from typing just before the copy is already in
      // `formData`, so it is in the snapshot and in what Keep saves.
      debouncedSave.cancel();

      // Read the answers as they stand now, not as they stood when the
      // clinician picked the source: the preview is a round trip, and anything
      // answered while it was in flight would otherwise be overwritten by the
      // older snapshot — and be missing from `preCarryoverAnswers`, so Discard
      // could not bring it back either.
      const current = useOASISFormStore.getState().formData;

      const next: OASISFormData = { ...current };
      copyableQids.forEach(qid => delete next[qid]);
      Object.assign(next, answers as OASISFormData);

      setPreCarryoverAnswers(current);
      setCarryoverQids(new Set(Object.keys(answers)));
      setCarryoverSourceLabel(label);
      setFormData(next);
      reportEdit();
      if (showInlineValidation) debouncedInlineValidation(next);
    },
    [
      debouncedSave,
      setFormData,
      reportEdit,
      showInlineValidation,
      debouncedInlineValidation,
    ]
  );

  const clearCarryover = () => {
    setPreCarryoverAnswers(null);
    setCarryoverSourceLabel(null);
    setCarryoverQids(new Set());
  };

  /** Commit the copied answers in a single immediate save (not debounced). */
  const handleKeepStaged = async () => {
    if (!carryoverPending) return;

    debouncedSave.cancel();
    const outcome = await saveFormData(formData);

    // `saveFormData` swallows every failure — a declined signature-invalidation
    // dialog, and a network or server error alike. Neither reached the server,
    // so the copy stays pending: the banner stays up, Sign stays blocked, and
    // the clinician can retry Keep. Clearing it here would say "saved" about
    // answers that live only in this browser tab, and would let the visit be
    // signed with the older answers on the record.
    if (outcome !== 'saved') return;
    clearCarryover();
  };

  /** Put back the answers as they stood before the copy, and persist them. */
  const handleDiscardStaged = async () => {
    if (!preCarryoverAnswers) return;

    debouncedSave.cancel();
    const restored = preCarryoverAnswers;
    setFormData(restored);
    if (showInlineValidation) debouncedInlineValidation(restored);

    // Autosave was held back for the whole pending window, so the server may
    // still be missing edits the clinician made just before copying. Those are
    // in the snapshot, so saving it restores the record as well as the screen.
    //
    // Clear the copy only once that landed, for the same reason Keep does: a
    // failed save leaves the screen showing the restored answers while the
    // record still holds older ones. Dropping the banner there would re-enable
    // Sign over that gap, and the state would persist until some later edit
    // happened to autosave. The banner staying up is the retry.
    const outcome = await saveFormData(restored);
    if (outcome !== 'saved') return;
    clearCarryover();
  };

  // Build each tab's lists once; panel picks the active tab without refiltering.
  const scrubberListModel = useMemo(() => {
    if (!showInlineValidation || !isOasis) return null;

    const totalFatalCount = inlineValidationErrors.filter(
      err => err.severity === 'fatal'
    ).length;
    const totalWarningCount = inlineValidationErrors.filter(
      err => err.severity === 'warning'
    ).length;

    const fatalsByTab = {} as Record<ScrubberTab, OasisValidationError[]>;
    const advisoryByTab = {} as Record<ScrubberTab, ScrubberListItem[]>;
    const tabCounts = {} as Record<ScrubberTab, number>;
    for (const tab of SCRUBBER_TABS) {
      fatalsByTab[tab] = buildFatalScrubberErrors(
        inlineValidationErrors,
        tab,
        currentCategory,
        qidToCategoryMap
      );
      advisoryByTab[tab] = buildAdvisoryScrubberItems(
        inlineValidationErrors,
        inlineContradictions,
        tab,
        currentCategory,
        qidToCategoryMap
      );
      // Dismissed QA findings still render on the QA tab but must not count
      // toward the badge, so this is not the list length.
      tabCounts[tab] =
        fatalsByTab[tab].length + countActiveScrubberItems(advisoryByTab[tab]);
    }

    return {
      totalFatalCount,
      totalWarningCount,
      fatalsByTab,
      advisoryByTab,
      tabCounts,
    };
  }, [
    showInlineValidation,
    isOasis,
    inlineValidationErrors,
    inlineContradictions,
    currentCategory,
    qidToCategoryMap,
  ]);

  /** Carryover affordance, or the staged-answers bar once answers are copied. */
  const renderCarryoverBar = () => {
    if (!carryoverEnabled) return null;

    if (!carryoverPending) {
      return (
        <div className='mb-3 flex justify-end'>
          <CarryoverSourceMenu
            visitId={visitId}
            onAnswersLoaded={handleCarryoverLoaded}
          />
        </div>
      );
    }

    return (
      <CarryoverStagedBanner
        count={carryoverQids.size}
        sourceLabel={carryoverSourceLabel}
        onKeep={() => void handleKeepStaged()}
        onDiscard={() => void handleDiscardStaged()}
      />
    );
  };

  const renderInlineValidationPanel = () => {
    if (!scrubberListModel) return null;

    const {
      totalFatalCount,
      totalWarningCount,
      fatalsByTab,
      advisoryByTab,
      tabCounts,
    } = scrubberListModel;
    const visibleFatals = fatalsByTab[scrubberTab];
    const visibleAdvisory = advisoryByTab[scrubberTab];

    const visibleTabs = SCRUBBER_TAB_DEFINITIONS.filter(
      definition => definition.id !== 'qa' || qaTabEnabled
    );
    const scrubberNoIssuesMessage =
      visibleTabs.find(definition => definition.id === scrubberTab)
        ?.emptyMessage ?? 'No issues';

    const hasVisibleBodyItems =
      visibleFatals.length > 0 || visibleAdvisory.length > 0;
    return (
      // Pinned to the top of the scrollport so the counts and the section
      // filter stay reachable while editing fields far down the form.
      // Collapsed is the default and costs about one row of height.
      //
      // The outer band is opaque because form fields scroll underneath it; the
      // card keeps its own translucent tint so the surface looks unchanged.
      //
      // Below `md` the page scroller is the app shell's `main`, which carries
      // its own padding, and a scroll container's padding insets where sticky
      // children may pin. Pinning that much higher and carrying a band of the
      // same height covers the strip fields would otherwise scroll through; the
      // matching negative margin keeps the resting layout where it was. From
      // `md` up the panel's scroll box has no padding, so none of that applies.
      <div
        ref={scrubberPanelRef}
        className='sticky -top-4 -mt-4 pt-4 md:top-0 md:mt-0 md:pt-0 z-20 bg-background pb-3'
      >
        <div className='rounded-md border bg-muted/20 p-3'>
          {/* Toggle is separate from section-filter buttons so keyboard events
            on Section/Misc/All do not also collapse the scrubber. */}
          <div className='w-full flex flex-wrap items-center justify-between gap-x-3 gap-y-1.5'>
            <button
              type='button'
              onClick={() => setIsScrubberExpanded(prev => !prev)}
              className='flex items-center gap-2 text-sm min-w-0 text-left cursor-pointer'
              aria-expanded={isScrubberExpanded}
              aria-label='Toggle Live Scrubber'
            >
              <AlertTriangle className='h-4 w-4 text-amber-600' />
              <span className='font-medium'>Live Scrubber</span>
              {inlineValidationLoading ? (
                <span className='text-xs text-muted-foreground'>
                  Checking...
                </span>
              ) : inlineValidationErrorMessage ? (
                <span className='text-xs text-destructive'>
                  Validation unavailable
                </span>
              ) : (
                <span className='text-xs text-muted-foreground'>
                  Checks run automatically
                </span>
              )}
              {isScrubberExpanded ? (
                <ChevronUp className='h-4 w-4 text-muted-foreground' />
              ) : (
                <ChevronDown className='h-4 w-4 text-muted-foreground' />
              )}
            </button>
            <div className='flex flex-wrap items-center gap-2'>
              {/* Stays put once validation has run, even at zero issues — the
                empty-state copy is tab-scoped, so hiding the pills would leave
                no way to tell which tab it is describing.*/}
              {hasValidatedOnce && (
                <div className='flex flex-wrap gap-0.5'>
                  {visibleTabs.map(({ id: tab, label }) => {
                    const count = tabCounts[tab];
                    return (
                      <button
                        key={tab}
                        type='button'
                        onClick={() => {
                          setScrubberTab(tab);
                          if (!isScrubberExpanded) setIsScrubberExpanded(true);
                        }}
                        className={`px-1.5 py-0.5 rounded text-[10px] font-medium transition-colors ${
                          scrubberTab === tab
                            ? 'bg-foreground/10 text-foreground'
                            : 'text-muted-foreground hover:text-foreground/70'
                        }`}
                      >
                        {label ??
                          resolveCategoryLabel(
                            currentCategory || '',
                            labelByCategory
                          )}
                        {count > 0 && (
                          <span className='ml-0.5 opacity-70'>({count})</span>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
              {(totalFatalCount > 0 || totalWarningCount > 0) && (
                <div className='flex items-center gap-2 text-xs'>
                  {totalFatalCount > 0 && (
                    <span className='rounded bg-destructive/10 px-2 py-0.5 text-destructive'>
                      {totalFatalCount} fatal
                    </span>
                  )}
                  {totalWarningCount > 0 && (
                    <span className='rounded bg-amber-100 px-2 py-0.5 text-amber-700'>
                      {totalWarningCount} warning
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>
          {isScrubberExpanded && (
            <div
              ref={scrubberExpandedBodyRef}
              className='mt-2 overflow-y-auto'
              style={{
                maxHeight: scrubberBodyMaxHeight,
                ...(inlineValidationLoading &&
                scrubberStableBodyHeightRef.current > 0
                  ? {
                      minHeight: Math.min(
                        scrubberStableBodyHeightRef.current,
                        scrubberBodyMaxHeight
                      ),
                    }
                  : {}),
              }}
            >
              {inlineValidationLoading ? (
                <div className='flex items-center gap-2 text-xs text-muted-foreground'>
                  <Loader2 className='h-3.5 w-3.5 shrink-0 animate-spin' />
                  Running live checks...
                </div>
              ) : inlineValidationErrorMessage ? (
                <p className='text-xs text-destructive'>
                  {inlineValidationErrorMessage}
                </p>
              ) : hasVisibleBodyItems ? (
                <div className='space-y-2'>
                  {visibleFatals.map((err, idx) => (
                    <CmsIssueCard
                      key={`fatal-${err.edit_id}-${idx}`}
                      error={err}
                      qidToQuestionText={qidToQuestionText}
                      onJump={navigateToError}
                    />
                  ))}
                  {visibleAdvisory.map((item, idx) =>
                    item.kind === 'cms' ? (
                      <CmsIssueCard
                        key={`warning-${item.error.edit_id}-${idx}`}
                        error={item.error}
                        qidToQuestionText={qidToQuestionText}
                        onJump={navigateToError}
                      />
                    ) : (
                      <ContradictionFindingCard
                        key={item.finding.rule_id}
                        finding={item.finding}
                        currentCategory={currentCategory}
                        qidToCategoryMap={qidToCategoryMap}
                        labelByCategory={labelByCategory}
                        onHighlightLocation={navigateToContradictionLocation}
                        onSetDismissed={setContradictionDismissed}
                      />
                    )
                  )}
                </div>
              ) : hasValidatedOnce ? (
                <div className='text-xs text-muted-foreground'>
                  {scrubberNoIssuesMessage}
                </div>
              ) : (
                <div className='text-xs text-muted-foreground'>
                  Running checks for loaded data.
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  // What the form renders. Hydration, saving and validation read `schema`.
  const renderSchema = useMemo(
    () =>
      schema && codesLocked ? withBillingCodeQuestionsLocked(schema) : schema,
    [schema, codesLocked]
  );

  const currentQuestions = useMemo(
    () =>
      (currentCategory && renderSchema ? renderSchema[currentCategory] : []) ||
      [],
    [currentCategory, renderSchema]
  );

  const formQuestions = useMemo(
    () => (renderSchema ? Object.values(renderSchema).flat() : []),
    [renderSchema]
  );

  const schemaQuestionOptions: SchemaQuestionOption[] = useMemo(() => {
    if (!schema) return [];
    const options: SchemaQuestionOption[] = [];
    const seen = new Set<string>();

    const collect = (questions: Question[], category: string) => {
      for (const q of questions) {
        if (!seen.has(q.qid)) {
          seen.add(q.qid);
          options.push({
            qid: q.qid,
            label: q.question || q.qid,
            category,
          });
        }
        if (q.subitems) {
          for (const children of Object.values(q.subitems)) {
            collect(children, category);
          }
        }
      }
    };

    for (const [category, questions] of Object.entries(schema)) {
      collect(questions, category);
    }
    return options;
  }, [schema]);

  const gridItems = useMemo(
    () => flattenQuestions(currentQuestions),
    [currentQuestions]
  );

  // Minimal visit shape for the embedded goals section. Built from primitives so
  // the memoized form does not take the whole (frequently-replaced) visit object
  // as a prop.
  const goalsVisit: GoalsTabVisit = useMemo(
    () => ({
      id: visitId,
      status: visitStatus,
      episode_closed: episodeClosed,
      visit_type: visitType,
      episode_id: episodeId,
      episode_start_date: episodeStartDate,
      episode_end_date: episodeEndDate,
    }),
    [
      visitId,
      visitStatus,
      episodeClosed,
      visitType,
      episodeId,
      episodeStartDate,
      episodeEndDate,
    ]
  );

  // Patient names are only shown in GoalsTab's dialog header, which inline mode
  // hides, so empty strings are safe here.
  const renderGoalsSection = () => (
    <GoalsTab
      open
      onClose={noop}
      patientFirstName=''
      patientLastName=''
      visit={goalsVisit}
      inline
      capabilities={visitGoalCapabilities(visitType)}
      discipline={getDisciplineForVisitType(visitType)}
    />
  );

  // The falls and infections module, plus the caveat WellSky also prints: these
  // are patient-level incident records, not answers on this note, so the
  // generated PDF does not carry them.
  const renderEventsSection = () => (
    <>
      <PatientEvents
        patientId={patientId}
        agencyClinicId={agencyClinicId}
        episodeId={episodeId}
        episodeStartDate={episodeStartDate}
        episodeEndDate={episodeEndDate}
      />
      <p className='mt-2 text-xs text-muted-foreground'>
        Fall and infection logs are not included in the printed note.
      </p>
    </>
  );

  // PatientMedications requires a handler; forward to the parent when provided.
  const handleMedicationOrderCreated = useCallback(
    (order: OrderResponse) => onMedicationOrderCreated?.(order),
    [onMedicationOrderCreated]
  );

  if (isLoading) {
    return (
      <div className='flex items-center justify-center py-12'>
        <Loader2 className='h-6 w-6 animate-spin text-muted-foreground' />
      </div>
    );
  }

  if (error) {
    return <p className='text-destructive text-sm py-4'>{error}</p>;
  }

  if (!schema || categories.length === 0) {
    return (
      <p className='text-muted-foreground text-sm py-4'>
        No form schema available for this visit type.
      </p>
    );
  }

  const renderGridItem = (item: GridItem) => {
    const spanClass = SPAN_CLASSES[item.span];

    return (
      <div key={item.question.qid} className={`${spanClass} min-w-0`}>
        <OASISQuestionRenderer
          question={item.question}
          value={formData[item.question.qid]}
          onChange={value => {
            const updates: Partial<OASISFormData> = {
              [item.question.qid]: value,
            };

            const clearUpdates = getSubitemClearUpdates(
              item.question,
              value,
              formData
            );
            Object.assign(updates, clearUpdates);

            handleFormDataChange(updates);
          }}
          formData={formData}
          containerSpan={item.span}
          onFormDataChange={handleFormDataChange}
          highlightedQid={highlightedQid}
          stagedQids={carryoverQids}
        />
      </div>
    );
  };

  // One copy of each module's props. `wireScrubber` re-validates the Live
  // Scrubber on change, which only does anything on OASIS visits.
  const renderMedicationsModule = (wireScrubber: boolean) => (
    <PatientMedications
      patientId={patientId}
      episodeId={episodeId}
      visitId={visitId}
      visitStatus={visitStatus}
      episodeClosed={episodeClosed}
      onMedicationOrderCreated={handleMedicationOrderCreated}
      onMedicationsChanged={
        wireScrubber ? handleScrubberInputChanged : undefined
      }
    />
  );

  const renderWoundsModule = (wireScrubber: boolean) => (
    <PatientWounds
      patientId={patientId}
      visitId={visitId}
      visitStatus={visitStatus}
      episodeClosed={episodeClosed}
      onWoundsChanged={wireScrubber ? handleScrubberInputChanged : undefined}
    />
  );

  // Wounds and meds are surfaced at the bottom of their matching OASIS sections
  // so clinicians can review them before submitting the chart.
  const renderEmbeddedSectionModules = () => {
    const category = currentCategory || '';

    // The OASIS schema carries a Skin section for every discipline, but only the
    // visit types WellSky gives a wound section get the Wounds module.
    if (category === SKIN_SECTION_ID && supportsWounds(visitType)) {
      return (
        <div className='mt-6 pt-6 border-t border-border'>
          <h4 className='text-sm font-semibold text-foreground mb-3'>Wounds</h4>
          {/* Flat on purpose: Active / Discontinued tabs unmount hidden
              rows, and Live Scrubber healed-wound rules jump by
              discontinued id.*/}
          {renderWoundsModule(true)}
        </div>
      );
    }

    if (category === MEDICATIONS_SECTION_ID) {
      return (
        <>
          <div className='mt-6 pt-6 border-t border-border'>
            <h4 className='text-sm font-semibold text-foreground mb-3'>
              Medications
            </h4>
            {renderMedicationsModule(true)}
          </div>
          {patient && (
            <div className='mt-6 pt-6 border-t border-border'>
              <h4 className='text-sm font-semibold text-foreground mb-3'>
                Allergies
              </h4>
              <PatientAllergies patient={patient} />
            </div>
          )}
        </>
      );
    }

    return null;
  };

  const renderOasisSectionContent = () => {
    const BespokeComponent = resolveBespokeSection(
      bespokeDbConfig,
      schemaVersion?.name,
      currentCategory || ''
    );

    const standardGrid = (
      <QuestionGridFloor>
        <div className='grid grid-cols-12 gap-3'>
          {gridItems.map(item => renderGridItem(item))}
        </div>
      </QuestionGridFloor>
    );

    if (BespokeComponent && schemaVersion) {
      return (
        <>
          <div className='mb-4 pb-2 border-b border-border flex items-center justify-between'>
            <h3 className='text-base font-bold text-foreground'>
              {resolveCategoryLabel(currentCategory || '', labelByCategory)}
            </h3>
          </div>
          {renderCarryoverBar()}
          {renderInlineValidationPanel()}
          <QuestionGridFloor bespoke>
            <BespokeComponent
              answers={formData}
              onAnswerChange={(qid, value) =>
                handleFormDataChange({ [qid]: value })
              }
              onFormDataChange={handleFormDataChange}
              schema={currentQuestions}
              schemaQuestionOptions={schemaQuestionOptions}
              formSchema={formQuestions}
              sectionName={currentCategory || ''}
              externalAnswers={externalAnswers}
              fallback={standardGrid}
            />
          </QuestionGridFloor>
          {renderEmbeddedSectionModules()}
        </>
      );
    }

    return (
      <>
        <div className='mb-4 pb-2 border-b border-border flex items-center justify-between'>
          <h3 className='text-base font-bold text-foreground'>
            {resolveCategoryLabel(currentCategory || '', labelByCategory)}
          </h3>
        </div>
        {renderCarryoverBar()}
        {renderInlineValidationPanel()}

        {standardGrid}
        {renderEmbeddedSectionModules()}
      </>
    );
  };

  // ─── One-section form: a single page; section chrome → layout_json ───
  if (Object.keys(schema).length <= 1) {
    const wholeSectionKey = categories[0] ?? WHOLE_VISIT_SECTION_NAME;
    const categoryQuestions = (renderSchema ?? schema)[wholeSectionKey] || [];
    const items = flattenQuestions(categoryQuestions);
    const BespokeComponent = schemaVersion
      ? resolveBespokeSection(
          bespokeDbConfig,
          schemaVersion.name,
          wholeSectionKey
        )
      : null;

    const standardGrid = (
      <QuestionGridFloor>
        <div className='grid grid-cols-12 gap-3'>
          {items.map(item => renderGridItem(item))}
        </div>
      </QuestionGridFloor>
    );

    return (
      <CmsQuestionErrorsContext.Provider value={questionErrors}>
        <div ref={scrollportRef} className='min-w-0'>
          {renderCarryoverBar()}
          {renderInlineValidationPanel()}
          {BespokeComponent ? (
            <QuestionGridFloor bespoke>
              <BespokeComponent
                answers={formData}
                onAnswerChange={(qid, value) =>
                  handleFormDataChange({ [qid]: value })
                }
                onFormDataChange={handleFormDataChange}
                schema={categoryQuestions}
                schemaQuestionOptions={schemaQuestionOptions}
                formSchema={formQuestions}
                sectionName={wholeSectionKey}
                externalAnswers={externalAnswers}
                fallback={standardGrid}
              />
            </QuestionGridFloor>
          ) : (
            standardGrid
          )}
          {/* One-section forms are a single page, so the medications, allergies,
            wounds, and goals modules are surfaced inline at the bottom of the
            form, matching the OASIS section embeds. This lets clinicians review
            and edit them before submitting the chart instead of leaving the
            form to reach separate tabs. Each module self-gates editing by visit
            status, so it stays read-only where it is not eligible.

            Medications and allergies are scoped to skilled-nursing (RN/SN)
            routine notes: the single-page branch also renders PT/OT/ST/MSW/HHA
            notes, and those clinical modules do not belong on those disciplines'
            forms. Wounds and goals use their own per-visit-type lists instead,
            since WellSky puts a wound section on PT/OT notes but not on ST,
            supervisory, MSW, or aide notes. */}
          {isSNDiscipline(visitType) && (
            <>
              <div className='mt-6 pt-6 border-t border-border'>
                <h3 className='text-base font-bold text-foreground mb-3'>
                  Medications
                </h3>
                {renderMedicationsModule(isOasis)}
              </div>
              {patient && (
                <div className='mt-6 pt-6 border-t border-border'>
                  <h3 className='text-base font-bold text-foreground mb-3'>
                    Allergies
                  </h3>
                  <PatientAllergies patient={patient} />
                </div>
              )}
            </>
          )}
          {supportsWounds(visitType) && (
            <div className='mt-6 pt-6 border-t border-border'>
              <h3 className='text-base font-bold text-foreground mb-3'>
                Wounds
              </h3>
              {/* Flat on purpose — see the OASIS Skin embed. */}
              {renderWoundsModule(isOasis)}
            </div>
          )}
          {showsDiagnosisReview(visitType) && (
            <div className='mt-6 pt-6 border-t border-border'>
              <h3 className='text-base font-bold text-foreground mb-3'>
                Diagnoses
              </h3>
              {/* Read-only. This note displays the episode's diagnoses; it does
                not record them — see showsDiagnosisReview. */}
              <DiagnosisReview visitId={visitId} />
            </div>
          )}
          {showsGoalsSection(visitType) && (
            <div className='mt-6 pt-6 border-t border-border'>
              <h3 className='text-base font-bold text-foreground mb-3'>
                Goals &amp; Interventions
              </h3>
              {renderGoalsSection()}
            </div>
          )}
          {showsEventsSection(visitType) && (
            <div className='mt-6 pt-6 border-t border-border'>
              <h3 className='text-base font-bold text-foreground mb-3'>
                Events
              </h3>
              {renderEventsSection()}
            </div>
          )}
        </div>
      </CmsQuestionErrorsContext.Provider>
    );
  }
  return (
    <CmsQuestionErrorsContext.Provider value={questionErrors}>
      <div ref={scrollportRef} className='flex flex-col md:flex-row'>
        {/* Mobile: collapsible section picker */}
        <div className='md:hidden mb-4'>
          <button
            type='button'
            onClick={() => setIsMobileNavOpen(open => !open)}
            aria-expanded={isMobileNavOpen}
            aria-controls='oasis-mobile-section-list'
            className='w-full flex items-center justify-between gap-2 rounded-md border border-border bg-background px-3 py-2.5 text-sm font-medium text-left'
          >
            <span className='truncate'>
              {resolveCategoryLabel(currentCategory || '', labelByCategory)}
            </span>
            {isMobileNavOpen ? (
              <ChevronUp className='h-4 w-4 shrink-0 text-muted-foreground' />
            ) : (
              <ChevronDown className='h-4 w-4 shrink-0 text-muted-foreground' />
            )}
          </button>
          {isMobileNavOpen && (
            <div
              id='oasis-mobile-section-list'
              className='mt-2 rounded-md border border-border p-2 max-h-80 overflow-y-auto'
            >
              <OASISNavigation
                categories={categories}
                currentCategory={currentCategory}
                onCategoryChange={category => {
                  setIsMobileNavOpen(false);
                  handleCategoryChange(category);
                }}
                schema={schema}
                formData={formData}
                fatalCountByCategory={fatalCountByCategory}
                advisoryCountByCategory={advisoryCountByCategory}
                labelByCategory={labelByCategory}
              />
            </div>
          )}
        </div>

        {/* Sidebar navigation (desktop). Pinned to the top of the scrollport so
          sections stay switchable from anywhere in the form. The list is often
          taller than the scrollport, and a pinned element taller than its
          scrollport can never be scrolled into view, so it is capped to the
          measured height and scrolls within itself instead. */}
        <div className='hidden md:block w-48 flex-shrink-0 pr-4 border-r border-border'>
          {/* `top-5` mirrors the `p-5` on the scrolled content in
            `VisitDetailPanel`, so the list parks where it already sits rather
            than sliding that padding's worth on the first scroll. Same rem
            unit, so the two stay together if the root font size changes. Only
            this list can do it -- nothing shares its column, so the strip above
            stays empty, where the Live Scrubber would show form fields. */}
          <div
            className='sticky top-5 flex flex-col overflow-y-auto'
            style={
              scrollportHeight ? { maxHeight: scrollportHeight } : undefined
            }
          >
            <div className='flex items-center justify-between mb-2 px-2.5'>
              <span className='text-[10px] font-semibold text-muted-foreground uppercase tracking-wider'>
                Sections
              </span>
            </div>
            <OASISNavigation
              categories={categories}
              currentCategory={currentCategory}
              onCategoryChange={handleCategoryChange}
              schema={schema}
              formData={formData}
              fatalCountByCategory={fatalCountByCategory}
              advisoryCountByCategory={advisoryCountByCategory}
              labelByCategory={labelByCategory}
            />
          </div>
        </div>

        {/* Form content */}
        <div className='flex-1 min-w-0 md:pl-5'>
          {currentCategory === ACTIVE_DIAGNOSIS_CATEGORY ? (
            <>
              <div className='mb-4 pb-2 border-b border-border'>
                <h3 className='text-base font-bold text-foreground'>
                  Active Diagnosis
                </h3>
              </div>
              {/* Same Live Scrubber chrome as OASIS sections — including CMS
                fatal mode, even though Active Diagnosis has no CMS qids. */}
              {renderCarryoverBar()}
              {renderInlineValidationPanel()}
              <VisitActiveDiagnosis
                visitId={visitId}
                onActiveDiagnosesSaved={handleScrubberInputChanged}
                codingDateKey={savedM0090}
              />
            </>
          ) : currentCategory === GOALS_CATEGORY ? (
            <>
              <div className='mb-4 pb-2 border-b border-border'>
                <h3 className='text-base font-bold text-foreground'>
                  Goals &amp; Interventions
                </h3>
              </div>
              {renderGoalsSection()}
            </>
          ) : currentCategory === EVENTS_CATEGORY ? (
            <>
              <div className='mb-4 pb-2 border-b border-border'>
                <h3 className='text-base font-bold text-foreground'>Events</h3>
              </div>
              {renderEventsSection()}
            </>
          ) : currentCategory === MEDICATIONS_CATEGORY ? (
            <>
              <div className='mb-4 pb-2 border-b border-border'>
                <h3 className='text-base font-bold text-foreground'>
                  Medications
                </h3>
              </div>
              {renderMedicationsModule(true)}
              {patient && (
                <div className='mt-6 pt-6 border-t border-border'>
                  <h4 className='text-sm font-semibold text-foreground mb-3'>
                    Allergies
                  </h4>
                  <PatientAllergies patient={patient} />
                </div>
              )}
            </>
          ) : currentCategory === WOUNDS_CATEGORY ? (
            <>
              <div className='mb-4 pb-2 border-b border-border'>
                <h3 className='text-base font-bold text-foreground'>Wounds</h3>
              </div>
              {/* Flat on purpose — see the Skin embed. */}
              {renderWoundsModule(true)}
            </>
          ) : currentCategory === DIAGNOSIS_REVIEW_CATEGORY ? (
            <>
              <div className='mb-4 pb-2 border-b border-border'>
                <h3 className='text-base font-bold text-foreground'>
                  Diagnoses
                </h3>
              </div>
              {/* Read-only — see showsDiagnosisReview. */}
              <DiagnosisReview visitId={visitId} />
            </>
          ) : (
            renderOasisSectionContent()
          )}
        </div>
      </div>
    </CmsQuestionErrorsContext.Provider>
  );
};

/**
 * Loads the agency's template library and publishes it to the text fields.
 *
 * A component of its own rather than a hook called in the wrapper below, so the
 * fetch state lives here: state in the memoized wrapper would re-render the
 * whole question tree when the library arrives, which is the cost the memo
 * exists to avoid.
 */
function FormTemplatesLoader({ children }: { children: React.ReactNode }) {
  const templates = useAgencyFormTemplates();
  return (
    <FormTemplatesProvider templates={templates}>
      {children}
    </FormTemplatesProvider>
  );
}

/**
 * Memoized so the parent's per-keystroke status state updates do not
 * re-render the whole question tree; keep the function props stable.
 *
 * The clinic and template providers wrap the form rather than living inside it
 * so every early return is covered by one mount.
 */
export const OASISForm = React.memo(function OASISForm(props: OASISFormProps) {
  return (
    <ClinicProvider agencyClinicId={props.agencyClinicId ?? null}>
      <FormTemplatesLoader>
        <OASISFormComponent {...props} />
      </FormTemplatesLoader>
    </ClinicProvider>
  );
});
OASISForm.displayName = 'OASISForm';
